import React from "react";
import { Formik, FormikActions, FormikProps, Field } from "formik";
import { styled } from "@material-ui/styles";
import { InputAdornment } from "@material-ui/core";
import { IStyledArguments } from "../../../types/styled-arguments";
import TextInput from "../../text-input";
import PasswordInput from "../../password-input";
import InputErrorMessage from "../../input-error-message";
import {
  FormattedMessage,
  InjectedIntlProps,
  injectIntl,
  InjectedIntl,
} from "react-intl";
import messages from "./LoginForm.intl";
import { VerticalForm, ActionButton } from "../../unauthenticated-layout-elements";
import { TicketSeam } from "../../molecules/cards";
import EmailIcon from "@material-ui/icons/EmailOutlined";
import LockIcon from "@material-ui/icons/LockOutlined";

const required = (intl: InjectedIntl) => (value: string) => {
  let errorMessage;
  if (!value) {
    errorMessage = intl.formatMessage(messages.requiredFieldError);
  }
  return errorMessage;
};

const PageBackground = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  padding: theme.spacing(3),
}));

const Wrapper = styled("div")({
  width: "100%",
  maxWidth: 380,
});

const Card = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 22,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  overflow: "hidden",
}));

const HeaderZone = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
  padding: theme.spacing(4, 3.5, 3.25),
  textAlign: "center",
}));

const LogoBadge = styled("div")(({ theme }: IStyledArguments) => ({
  width: 60,
  height: 60,
  borderRadius: 16,
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "1.75rem",
  color: theme.palette.primary.contrastText,
}));

const Heading = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.4rem",
  color: theme.palette.text.primary,
  marginTop: theme.spacing(2),
}));

const FormZone = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3.5, 3, 3),
}));

const ForgotPasswordRow = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "right",
  marginTop: theme.spacing(-1),
  marginBottom: theme.spacing(1),
  "& a": {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.primary.dark,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const CreateAccountRow = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  marginTop: theme.spacing(3),
  fontSize: "0.85rem",
  color: theme.palette.text.secondary,
  "& a": {
    fontWeight: 600,
    color: theme.palette.primary.dark,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export interface ILoginFormValues {
  email: string;
  password: string;
}

interface ILoginForm extends InjectedIntlProps {
  onSubmit: (arg: ILoginFormValues) => void;
  hasLoginError: boolean;
}

const LoginForm = ({ onSubmit, intl, hasLoginError }: ILoginForm) => (
  <Formik
    initialValues={{ email: "", password: "" }}
    onSubmit={(
      values: ILoginFormValues,
      actions: FormikActions<ILoginFormValues>
    ) => {
      onSubmit(values);
      actions.setSubmitting(false);
    }}
    render={({ errors, touched }: FormikProps<ILoginFormValues>) => (
      <PageBackground>
        <Wrapper>
          <Card>
            <HeaderZone>
              <LogoBadge>F</LogoBadge>
              <Heading>
                <FormattedMessage {...messages.signIn} />
              </Heading>
            </HeaderZone>

            <TicketSeam />

            <FormZone>
              <VerticalForm data-testid="login-form">
                <Field
                  validate={required(intl)}
                  name="email"
                  error={hasLoginError || (errors.email && touched.email)}
                  component={TextInput}
                  fullWidth
                  inputProps={{
                    "data-testid": "email",
                    autoComplete: "email",
                    autoFocus: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  label={intl.formatMessage(messages.emailAddressLabel)}
                />
                <InputErrorMessage name="email" />

                <Field
                  validate={required(intl)}
                  name="password"
                  error={hasLoginError || (errors.password && touched.password)}
                  component={PasswordInput}
                  fullWidth
                  startIcon={<LockIcon fontSize="small" />}
                  inputProps={{
                    "data-testid": "password",
                    autoComplete: "current-password",
                  }}
                  label={intl.formatMessage(messages.passwordLabel)}
                />
                <InputErrorMessage name="password" />

                <ForgotPasswordRow>
                  <a data-testid="navigate-to-forgot-password" href="/forgot-password">
                    <FormattedMessage {...messages.forgotPassword} />
                  </a>
                </ForgotPasswordRow>

                <ActionButton data-testid="login-button">
                  <FormattedMessage {...messages.signInButton} />
                </ActionButton>
              </VerticalForm>
            </FormZone>
          </Card>

          <CreateAccountRow>
            <FormattedMessage {...messages.registerLinkMessage} />
            &nbsp;
            <a href="/register">
              <FormattedMessage {...messages.register} />
            </a>
          </CreateAccountRow>
        </Wrapper>
      </PageBackground>
    )}
  />
);

export default injectIntl(LoginForm);
